import type { Ref } from "vue";

import { callApi } from "@/api/apiInteraction";
import { ApiMethod } from "@/api/apiMethod";
import { constructExcelRequestItem } from "@/excel/constructExcelRequestItem";
import { setJsonAsTable } from "@/excel/excelOperations";
import type { ApiEntry } from "@/services/api-inventory/apiInventory.types";
import type {
  ExcelFunction,
  LoadedFunction,
  NonRegistrableFunction,
} from "@/services/custom-functions/customFunction.types";
import { createCustomFunctionUsageMonitor } from "@/services/custom-functions/customFunctionUsageMonitor";
import { appendQuery, isRecord, standardizeDecimalSeparator, validateAndFilterAddresses } from "@/utils/utils";

type InvocationContext = {
  address: string;
  parameterAddresses?: string[];
};

// callApi lets errors propagate as-is: the library rejects with a plain object, not an Error, and
// puts the parsed response body in statusText. Excel needs a readable string, so it's built here.
function getApiErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message || "An unexpected error occurred.";
  }

  const { status, statusText } = (error ?? {}) as { status?: number; statusText?: unknown };
  const detail = isRecord(statusText) ? statusText.detail : undefined;

  if (Array.isArray(detail) && detail.length > 0) {
    return detail
      .map((item) => {
        if (!isRecord(item)) {
          return String(item);
        }

        // loc[0] names the request part ("body", "query", ...) and is dropped. What remains is the
        // path to the field, and it is empty when the whole body failed rather than one field.
        const field = Array.isArray(item.loc) ? item.loc.slice(1).join(".") : "";
        return field ? `${field}: ${item.msg}` : String(item.msg ?? "");
      })
      .join(";\n");
  }

  return `API error: ${status ?? "unknown"}`;
}

export function createApiRegistrationModule(apiEntries: Ref<Map<string, ApiEntry>>) {
  const customFunctionUsageMonitor = createCustomFunctionUsageMonitor(apiEntries);

  function associateFunction(func: ExcelFunction): void {
    // CustomFunctions.associate callback args are provided by Excel runtime and are untyped.
    CustomFunctions.associate(func.id, async (...args: unknown[]) => {
      try {
        // Excel appends InvocationContext as the final callback argument by API contract.
        const invocation = args[args.length - 1] as InvocationContext;
        const rawArgs = args.slice(0, -1);

        const processedArgs = rawArgs.map((arg) => standardizeDecimalSeparator(arg));
        const requestItem = constructExcelRequestItem(processedArgs, func.parameters);

        const isQueryRequest = func.method === ApiMethod.GET;

        let responseItem = await callApi<unknown>(
          isQueryRequest ? appendQuery(func.url, requestItem) : func.url,
          isQueryRequest ? undefined : requestItem,
          func.method
        );

        if (responseItem !== null && typeof responseItem === "object") {
          responseItem = await setJsonAsTable(
            responseItem,
            invocation.address,
            validateAndFilterAddresses(invocation.parameterAddresses ?? [])
          );
        }
        customFunctionUsageMonitor.logFunctionUsage(func);

        return responseItem;
      } catch (error: unknown) {
        console.error(`Error calling API for function ${func.id}:`, error);
        const message = getApiErrorMessage(error);
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Error: ${message}`);
      }
    });
  }

  async function validateFunctionsForEntry(url: string, excelFunctions: ExcelFunction[]): Promise<void> {
    const entry = apiEntries.value.get(url);
    if (!entry) return;

    const registrable: LoadedFunction[] = [];
    const nonRegistrable: NonRegistrableFunction[] = [];

    try {
      // Office readiness is guaranteed by runtime startup flow; keep this as a defensive guard.
      await Office.addin.showAsTaskpane();

      for (const func of excelFunctions) {
        try {
          // CustomFunctionManager is not typed in @types/office-js.
          await (Excel as any).CustomFunctionManager.register(JSON.stringify({ functions: [func] }), "");
          registrable.push({
            ...func,
            registrationState: "loaded",
          });
        } catch (error: unknown) {
          // console.error(`Error validating function ${func.id}:`, error);
          nonRegistrable.push({
            ...func,
            registrationState: "non-registrable",
            reason: error instanceof Error ? error.message : `Excel rejected registration for ${func.id}`,
          });
        }
      }

      const latestEntry = apiEntries.value.get(url);
      if (!latestEntry) return;

      apiEntries.value.set(url, {
        ...latestEntry,
        registrableFunctions: registrable,
        nonRegistrableFunctions: nonRegistrable,
      });
    } catch (error: unknown) {
      console.error("Error validating custom functions:", error);
      const reason =
        error instanceof Error ? error.message : "Office initialization failed during function validation.";

      const latestEntry = apiEntries.value.get(url);
      if (!latestEntry) return;

      apiEntries.value.set(url, {
        ...latestEntry,
        registrableFunctions: [],
        nonRegistrableFunctions: excelFunctions.map((func) => ({
          ...func,
          registrationState: "non-registrable",
          reason,
        })),
      });
    }
  }

  async function registerAllFunctions(): Promise<void> {
    const allRegistrable: LoadedFunction[] = [];

    apiEntries.value.forEach((entry) => {
      allRegistrable.push(...entry.registrableFunctions);
    });

    if (allRegistrable.length === 0) {
      return;
    }

    try {
      // Office readiness is guaranteed by runtime startup flow; keep this as a defensive guard.
      await Office.addin.showAsTaskpane();

      // CustomFunctionManager is not typed in @types/office-js.
      await (Excel as any).CustomFunctionManager.register(JSON.stringify({ functions: allRegistrable }), "");

      allRegistrable.forEach((func) => associateFunction(func));
    } catch (error: unknown) {
      console.error("Error registering all custom functions:", error);
    }
  }

  return {
    validateFunctionsForEntry,
    registerAllFunctions,
  };
}
